import { useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  ActionSheetIOS,
} from "react-native";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { Feather } from "@expo/vector-icons";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import {
  uploadProductImage,
  deleteProductImage,
} from "../../utils/supabase/productImageService";
import { platformAlert, showAlert } from "../../utils/platformAlert";

type Props = {
  images: string[];
  onChange: (next: string[]) => void;
  vendorId?: string | null;
  max?: number;
  enableCrop?: boolean;
  cropAspect?: [number, number];
};

export default function ImagePickerStrip({
  images,
  onChange,
  vendorId,
  max = 5,
  enableCrop = true,
  cropAspect = [1, 1],
}: Props) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [loading, setLoading] = useState(false);

  const requestPermission = useCallback(async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Permission needed", "Please allow photo library access.");
      return false;
    }
    return true;
  }, []);

  const requestCameraPermission = useCallback(async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Permission needed", "Please allow camera access.");
      return false;
    }
    return true;
  }, []);

  // Core launcher that differs only by editing/aspect
  const launchPicker = useCallback(
    async (opts: { allowsEditing: boolean }) => {
      const ok = await requestPermission();
      if (!ok) return;
      const res = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: opts.allowsEditing,
        aspect: opts.allowsEditing ? cropAspect : undefined,
        quality: 0.9,
      });
      if (res.canceled) return;

      if (!res.assets || res.assets.length === 0) {
        Alert.alert("Error", "No image was selected.");
        return;
      }

      setLoading(true);
      try {
        const uri = res.assets[0].uri;
        if (!uri) throw new Error("No asset URI returned");
        const publicUrl = await uploadProductImage(uri, vendorId!);
        onChange([...images, publicUrl]);
      } catch (e) {
        console.error(e);
        Alert.alert("Error", "Couldn't upload image, please try again.");
      } finally {
        setLoading(false);
      }
    },
    [requestPermission, cropAspect, images, onChange, vendorId]
  );

  // Camera launcher
  const launchCamera = useCallback(
    async (opts: { allowsEditing: boolean }) => {
      const ok = await requestCameraPermission();
      if (!ok) return;
      const res = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: opts.allowsEditing,
        aspect: opts.allowsEditing ? cropAspect : undefined,
        quality: 0.9,
      });
      if (res.canceled) return;

      if (!res.assets || res.assets.length === 0) {
        Alert.alert("Error", "No image was captured.");
        return;
      }

      setLoading(true);
      try {
        const uri = res.assets[0].uri;
        if (!uri) throw new Error("No asset URI returned");
        const publicUrl = await uploadProductImage(uri, vendorId!);
        onChange([...images, publicUrl]);
      } catch (e) {
        console.error(e);
        Alert.alert("Error", "Couldn't upload image, please try again.");
      } finally {
        setLoading(false);
      }
    },
    [requestCameraPermission, cropAspect, images, onChange, vendorId]
  );

  // Ask user how they want to pick: camera or library
  const handleAdd = useCallback(async () => {
    if (!vendorId) {
      Alert.alert("Please wait", "Loading vendor info...");
      return;
    }
    if (images.length >= max) {
      Alert.alert("Maximum Images", `You can only upload up to ${max} images.`);
      return;
    }

    // Simple choice: Take Photo or Choose from Library
    if (Platform.OS === "ios") {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          title: "Add Image",
          options: ["Take Photo", "Choose from Library", "Cancel"],
          cancelButtonIndex: 2,
        },
        (idx) => {
          if (idx === 0) launchCamera({ allowsEditing: false });
          else if (idx === 1) launchPicker({ allowsEditing: false });
        }
      );
    } else if (Platform.OS === "web") {
      // On web, just launch the file picker directly (no camera option)
      launchPicker({ allowsEditing: false });
    } else {
      // Android - show options dialog
      platformAlert("Add Image", "How would you like to add this image?", [
        { text: "Take Photo", onPress: () => launchCamera({ allowsEditing: false }) },
        { text: "Choose from Library", onPress: () => launchPicker({ allowsEditing: false }) },
        { text: "Cancel", style: "cancel" },
      ]);
    }
  }, [vendorId, images.length, max, launchPicker, launchCamera]);

  const handleRemove = useCallback(
    async (index: number) => {
      const url = images[index];
      try {
        if (url.includes("/product-images/")) {
          await deleteProductImage(url);
        }
      } catch (e) {
        console.warn("Delete error:", e);
      } finally {
        onChange(images.filter((_, i) => i !== index));
      }
    },
    [images, onChange]
  );

  return (
    <ScrollView
      horizontal
      style={styles.imageContainer}
      contentContainerStyle={styles.imageScrollContent}
      showsHorizontalScrollIndicator={false}
    >
      {images.map((img, i) => (
        <View key={i} style={styles.imageWrapper}>
          <Image 
            source={{ uri: img }} 
            style={styles.image}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
          />
          <TouchableOpacity
            style={styles.removeImageButton}
            onPress={() => handleRemove(i)}
          >
            <Feather name="x" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      ))}

      {images.length < max && (
        <TouchableOpacity
          style={[styles.imageWrapper, styles.addImageButton]}
          onPress={handleAdd}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator />
          ) : (
            <>
              <Feather name="plus" size={32} color={colors.textLight} />
              <Text style={styles.addImageText}>
                Add Image {images.length > 0 ? `(${images.length}/${max})` : ""}
              </Text>
              {enableCrop && (
                <Text style={styles.hint}>
                  Original or crop (aspect {cropAspect[0]}:{cropAspect[1]})
                </Text>
              )}
            </>
          )}
        </TouchableOpacity>
      )}
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    imageContainer: {
      height: 184, 
    },
    imageScrollContent: { 
      paddingHorizontal: spacing.lg, 
      paddingTop: spacing.sm,
      paddingBottom: spacing.md,
      gap: 12,
    },
    imageWrapper: {
      width: 160,
      height: 160,
      borderRadius: 16, // More rounded
      overflow: "hidden",
      position: "relative",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: "center",
      justifyContent: "center",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    image: { height: 160, width: 160 },
    removeImageButton: {
      position: "absolute",
      top: 8,
      right: 8,
      backgroundColor: "rgba(0, 0, 0, 0.6)", // Semi-transparent black
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    addImageButton: { 
      borderStyle: "dashed",
      backgroundColor: colors.surface,
    },
    addImageText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.textLight,
      marginTop: 8,
      textAlign: "center",
    },
    hint: {
      fontSize: 10,
      marginTop: 4,
      color: colors.textLight,
      opacity: 0.7,
      textAlign: "center",
      paddingHorizontal: 8,
    },
  });
